import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Info, X } from 'lucide-react';

/**
 * Ícone ⓘ que abre um balão com a explicação. O balão vai para o body (fixed),
 * senão seria cortado pelos containers com rolagem do painel e do cartão do node.
 */
export function InfoTip({ text, title }: { text: ReactNode; title?: string }) {
    const [style, setStyle] = useState<CSSProperties | null>(null);
    const trigger = useRef<HTMLSpanElement>(null);
    const pop = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!style) return;
        const close = (e: Event) => {
            const t = e.target as Node;
            if (pop.current?.contains(t) || trigger.current?.contains(t)) return;
            setStyle(null);
        };
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setStyle(null);
        const onScroll = (e: Event) => { if (!pop.current?.contains(e.target as Node)) setStyle(null); };
        document.addEventListener('mousedown', close);
        document.addEventListener('keydown', onKey);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onScroll);
        return () => {
            document.removeEventListener('mousedown', close);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onScroll);
        };
    }, [style]);

    const toggle = (e: React.SyntheticEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (style) { setStyle(null); return; }
        const r = trigger.current!.getBoundingClientRect();
        const width = 300;
        const left = Math.min(Math.max(8, r.left + r.width / 2 - width / 2), window.innerWidth - width - 8);
        const below = r.bottom + 240 < window.innerHeight;
        setStyle(below
            ? { position: 'fixed', top: r.bottom + 6, left, width }
            : { position: 'fixed', bottom: window.innerHeight - r.top + 6, left, width });
    };

    return (
        <>
            <span
                ref={trigger}
                role="button"
                tabIndex={0}
                aria-label="O que é isto?"
                onClick={toggle}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggle(e)}
                className={`nodrag nopan inline-flex items-center justify-center align-middle cursor-pointer rounded-full ${style ? 'text-blue-600' : 'text-gray-300 hover:text-blue-500'}`}
            >
                <Info className="w-3 h-3" />
            </span>
            {style && createPortal(
                <div
                    ref={pop}
                    style={{ ...style, zIndex: 10000 }}
                    className="rounded-lg bg-gray-900 text-white shadow-2xl px-3 py-2.5 text-[12px] leading-relaxed normal-case tracking-normal font-normal"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-start gap-2">
                        <div className="flex-1">
                            {title && <div className="font-semibold mb-0.5">{title}</div>}
                            <div className="text-gray-200">{text}</div>
                        </div>
                        <button type="button" onClick={() => setStyle(null)} className="text-gray-400 hover:text-white shrink-0" aria-label="Fechar">
                            <X className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>,
                document.body,
            )}
        </>
    );
}

/** Textos das explicações — um lugar só para ajustar. */
export const INFO = {
    leads: 'Pessoas diferentes que receberam pelo menos uma mensagem deste funil no período. Cada lead conta uma vez, mesmo que tenha passado pelo funil várias vezes.',
    enviados: 'Mensagens que a Meta aceitou. Conta repetições: se o lead mandou a palavra-chave de novo e o funil reiniciou, a mensagem conta de novo — por isso passa do número de leads. "Aceita" não é "entregue": o flow não recebe confirmação de entrega da Meta.',
    falhas: 'Mensagens que a Meta recusou (551, 10, 2022…). O lead não recebe e o funil para naquele node. A % é sobre todas as tentativas de envio.',
    parados: 'Leads que passaram por este funil no período e cujo último node alcançado é aquele. Inclui quem não clicou, quem clicou só no link, quem teve erro e quem terminou o funil. "Desde sempre" ignora o período; "em delay" são os que esperam um delay vencer.',
    cliques: 'Cliques em botões e na mídia dos nodes no período. A mesma pessoa pode clicar várias vezes — "de X leads" é quantas pessoas diferentes clicaram.',
    ctr: 'CTR por lead = leads que clicaram ÷ leads que chegaram: de cada 100 pessoas, quantas clicaram. "Por envio" = todos os cliques ÷ todas as mensagens, contando repetições — por isso costuma ser diferente.',
    sucesso: 'Enviados ÷ (enviados + falhas): quanto das tentativas de envio a Meta aceitou.',
    paginas: 'Páginas vinculadas ao funil que estão ativas, do total vinculado. Com o filtro de página ligado, mostra 1.',
    filtroPagina: 'Escolha uma página para ver só os números dela — nos KPIs, na lista de nodes, na faixa em cima de cada node, nos botões e no cartão do node. "Funil inteiro" volta a mostrar tudo.',
    abaNodes: 'Os nodes do funil com os números de cada um. Clique num node para a tela ir até ele e abrir o detalhe ao lado.',
    abaPaginas: 'Cada página do funil com leads, envios, falhas, cliques e CTR por lead. Clique numa página para filtrar tudo por ela.',
    botaoTop: 'O botão com mais cliques no período, somando todos os nodes. Clique para ir até o node dele.',
    maisParam: 'O node com mais leads parados (incluindo os em delay). Geralmente é onde o funil perde mais gente. Clique para ir até ele.',
    maisFalhas: 'O node com mais mensagens recusadas pela Meta, com os erros mais comuns. Clique para ir até ele e ver o que cada erro significa.',
    paginaTop: 'A página com maior CTR por lead entre as páginas do funil com pelo menos 50 leads no período. "filtrar" mostra só os números dela.',
    grafico: 'Envios, cliques e falhas por dia (eixo da esquerda) e o CTR por envio do dia (tracejado, eixo da direita). O dia é contado em UTC: o que acontece depois das 21h de Brasília entra no dia seguinte.',
    falhasCausa: 'Todas as falhas do período agrupadas pelo código de erro da Meta. "Sem código" são falhas registradas antes de 18/09, quando o sistema ainda não guardava o código.',
    chegaram: 'Pessoas diferentes que receberam (ou tentaram receber) este node no período.',
    enviadosNode: 'Mensagens deste node que a Meta aceitou. Passa de "Chegaram" quando o mesmo lead recebe o node de novo (reiniciou o funil).',
    falhasNode: 'Tentativas de envio deste node que a Meta recusou. O lead não recebe e o funil para aqui.',
    cliquesNode: 'Cliques nos botões e na mídia deste node. "de X leads" é quantas pessoas diferentes clicaram.',
    ctrNode: 'Por lead: leads que clicaram ÷ leads que chegaram a este node. Por envio: cliques ÷ mensagens enviadas deste node.',
    paradosNode: 'Leads do período cujo último node alcançado é este. "Desde sempre" conta todos, sem o filtro de período.',
    secaoFalhas: 'Cada código de erro que a Meta devolveu neste node, o que ele significa em português e quantas pessoas diferentes foram afetadas.',
    secaoDestino: 'O que acontece com quem clica em cada botão. Botão de resposta (postback) leva a outro node; botão de link abre o site e NÃO avança o funil; clique na mídia também não avança. "Botões excluídos" junta os cliques de botões que foram apagados do node. Número = cliques; % = CTR por envio do node.',
    secaoParados: 'Por que os leads pararam neste node. Cada motivo tem sua explicação; clique num motivo para filtrar a lista de leads abaixo.',
    secaoLeads: 'Os leads mais recentes parados aqui, com o motivo (cor da bolinha), a página, o erro quando houve e a hora do último envio. INATIVO = lead desativado.',
} as const;
